import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCheckCircle,
  faSpinner,
  faUserPlus,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import MatterService from '@frontend/AppSettings/services/MatterService';
import toastService from '@frontend/AppSettings/services/ToastService';
import './AddTeamMemberModal.css';

interface AddTeamMemberModalProps {
  matterId: string;
  currentEmployeeIds: string[];
  onClose: () => void;
  onAdded: () => void;
}

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const toName = (first: any, last: any) =>
  `${first ?? ''} ${last ?? ''}`.trim();

const isValidName = (name: string) =>
  name.length > 0 && !/^null\s+null$/i.test(name);

const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
  matterId,
  currentEmployeeIds,
  onClose,
  onAdded,
}) => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const data = await EmployeeService.getEmployees();
        setEmployees(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setError('Failed to load employees.');
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  const currentSet = new Set(currentEmployeeIds);
  const availableEmployees = employees.filter((emp) => {
    if (currentSet.has(emp.EmployeeID)) return false;
    const name = toName(emp.FirstName, emp.LastName);
    return isValidName(name);
  });
  const filtered = availableEmployees.filter((emp) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    const name = `${emp.FirstName || ''} ${emp.LastName || ''}`.trim().toLowerCase();
    const email = (emp.Email || '').toLowerCase();
    const id = (emp.EmployeeID || '').toLowerCase();
    return name.includes(s) || email.includes(s) || id.includes(s);
  });

  const toggleSelect = (employeeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selectedIds.size === 0) {
      setError('Please select at least one employee.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await MatterService.updateAssociatedEmployees(
        matterId,
        'add',
        Array.from(selectedIds)
      );
      toastService.success(
        selectedIds.size === 1 ? 'Team member added.' : 'Team members added.'
      );
      onAdded();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add team members.');
      toastService.error('Failed to add team members.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="add-team-member-overlay" onClick={onClose}>
      <div
        className="add-team-member-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="add-team-member-header">
          <div className="add-team-member-title-row">
            <FontAwesomeIcon icon={faUserPlus} className="add-team-member-icon" />
            <div>
              <h2 className="add-team-member-title">Add Team Members</h2>
              <p className="add-team-member-subtitle">
                Select employees to add to this matter
              </p>
            </div>
          </div>
          <button
            type="button"
            className="add-team-member-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="add-team-member-content">
          <div className="add-team-member-search">
            <FontAwesomeIcon icon={faSearch} className="add-team-member-search-icon" />
            <input
              type="text"
              className="add-team-member-search-input"
              placeholder="Search by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="add-team-member-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading employees...</span>
            </div>
          ) : (
            <div className="add-team-member-list">
              {filtered.length === 0 ? (
                <div className="add-team-member-empty">
                  {availableEmployees.length === 0
                    ? 'All employees are already on the team.'
                    : 'No employees match your search.'}
                </div>
              ) : (
                filtered.map((emp) => {
                  const name = `${emp.FirstName || ''} ${emp.LastName || ''}`.trim() || 'Unknown';
                  const id = emp.EmployeeID;
                  const isSelected = selectedIds.has(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`add-team-member-item ${isSelected ? 'add-team-member-item-selected' : ''}`}
                      onClick={() => toggleSelect(id)}
                    >
                      <div className="add-team-member-avatar">
                        {getInitials(name)}
                      </div>
                      <div className="add-team-member-info">
                        <div className="add-team-member-name">{name}</div>
                        <div className="add-team-member-details">
                          {emp.Email && (
                            <span className="add-team-member-email">{emp.Email}</span>
                          )}
                          <span className="add-team-member-id">ID: {id}</span>
                        </div>
                      </div>
                      {isSelected && (
                        <FontAwesomeIcon icon={faCheckCircle} className="add-team-member-check" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}

          {error && <div className="add-team-member-error">{error}</div>}
        </div>

        <div className="add-team-member-footer">
          <button
            type="button"
            className="add-team-member-btn add-team-member-btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="add-team-member-btn add-team-member-btn-primary"
            onClick={handleAdd}
            disabled={saving || selectedIds.size === 0}
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Adding...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckCircle} />
                Add {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTeamMemberModal;
